import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from '../../lib/logger';
import apiService from '../../services/api';
import { loadVendorsFromFile } from '../../services/vendorDataLoader';
import type { Vendor } from '../../types/shared';

// Fetch all vendors
export const fetchVendors = createAsyncThunk(
  'vendors/fetchAll',
  async (filters?: { category?: string; search?: string; verified?: boolean }) => {
    try {
      const vendors = await apiService.getVendors(filters);
      return vendors;
    } catch (_error) {
      // Fallback to loading from JSON file
      logger.log('API unavailable, loading vendors from file');
      const vendors = await loadVendorsFromFile();

      // Apply filters if provided
      let filtered = vendors;
      if (filters?.category) {
        filtered = filtered.filter((v) => v.category === filters.category);
      }
      if (filters?.search) {
        const query = filters.search.toLowerCase();
        filtered = filtered.filter(
          (v) => v.name.toLowerCase().includes(query) || v.category.toLowerCase().includes(query)
        );
      }
      if (filters?.verified !== undefined) {
        filtered = filtered.filter((v) => v.verified === filters.verified);
      }

      return filtered;
    }
  }
);

// Fetch single vendor
export const fetchVendorById = createAsyncThunk('vendors/fetchById', async (id: string) => {
  const vendor = await apiService.getVendor(id);
  if (!vendor) {
    throw new Error('Vendor not found');
  }
  return vendor;
});

// Create new vendor (admin only)
export const createVendor = createAsyncThunk(
  'vendors/create',
  async (data: Omit<Vendor, 'id' | 'createdAt' | 'updatedAt'>) => {
    const vendor = await apiService.createVendor(data);
    if (!vendor) {
      throw new Error('Failed to create vendor');
    }
    return vendor;
  }
);

// Update vendor (admin only)
export const updateVendor = createAsyncThunk(
  'vendors/update',
  async ({ id, data }: { id: string; data: Partial<Vendor> }) => {
    const vendor = await apiService.updateVendor(id, data);
    if (!vendor) {
      throw new Error('Failed to update vendor');
    }
    return vendor;
  }
);

// Delete vendor (admin only)
export const deleteVendor = createAsyncThunk('vendors/delete', async (id: string) => {
  const success = await apiService.deleteVendor(id);
  if (!success) {
    throw new Error('Failed to delete vendor');
  }
  return id;
});

// Bulk import vendors (admin only)
export const bulkImportVendors = createAsyncThunk(
  'vendors/bulkImport',
  async (vendors: Array<Omit<Vendor, 'id' | 'createdAt' | 'updatedAt'>>) => {
    const imported = await apiService.bulkImportVendors(vendors);
    return imported;
  }
);
